import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/system";
import styles from "../LossToAction/intrRcovery/intr.module.css";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CustomPagination from "Samarth Module/components/CustomPagination/CustomPagination";
import ExpandRight from "../../../../assets/images/expandRight.svg";

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-head": {
    fontFamily: "Unilever Shilling",
    backgroundColor: "#3447d4",
    color: "#fff",
    textAlign: "center",
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #EAECF0",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.2,
    boxSizing: "border-box",
    verticalAlign: "middle",
  },
  "&.MuiTableCell-body": {
    fontFamily: "Unilever Shilling",
    fontSize: 12,
    fontWeight: 400,
    padding: "5.5px 8px",
    color: "#2B2B2B",
    border: "1px solid #EAECF0",
    boxSizing: "border-box",
    verticalAlign: "middle",
    backgroundColor: "transparent",
  },
}));

const normalizeLabel = (label = "") => String(label).trim().toLowerCase();

const labelsEqual = (a = "", b = "") => normalizeLabel(a) === normalizeLabel(b);

const SUB_COLUMN_PREFIX = "Sub_";

const isSubColumn = (label) =>
  typeof label === "string" && label.startsWith(SUB_COLUMN_PREFIX);

const getSubColumnMeta = (label) => {
  if (!isSubColumn(label)) {
    return null;
  }

  const rawLabel = label.slice(SUB_COLUMN_PREFIX.length);
  const separatorIndex = rawLabel.lastIndexOf("_");

  if (separatorIndex < 0) {
    return null;
  }

  return {
    parentLabel: rawLabel.slice(0, separatorIndex).trim(),
    childLabel: rawLabel.slice(separatorIndex + 1).trim(),
  };
};

const isSubColumnOfParent = (column, parentLabel) => {
  const meta = getSubColumnMeta(column?.label);

  if (!meta) {
    return false;
  }

  return labelsEqual(meta.parentLabel, parentLabel);
};

const isObjectLike = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getValueByLabel = (obj, label) => {
  if (!isObjectLike(obj)) return undefined;
  if (obj[label] !== undefined) return obj[label];

  const matchedKey = Object.keys(obj).find((key) => labelsEqual(key, label));
  return matchedKey ? obj[matchedKey] : undefined;
};

const EXPAND_COLUMN_WIDTH = 60;
const COLLAPSED_COL_WIDTH = 120;
const EXPANDED_COL_WIDTH = 250;
const CHILD_BATCH_SIZE = 20;

/* Helper functions to avoid nested ternary operations */
const getNewSortDirection = (isActive, currentDirection) => {
  if (!isActive) return "ASC";
  return currentDirection === "ASC" ? "DESC" : "ASC";
};

const getColumnBackgroundColor = (colIdx) => {
  return colIdx % 2 === 0 ? "rgb(194, 224, 255)" : "rgb(93, 110, 215)";
};

const getColumnTextColor = (colIdx) => {
  return colIdx % 2 === 0 ? "rgb(11, 31, 68)" : "rgb(255, 255, 255)";
};

/* Helper to render sort icons without nested ternary */
const renderSortIcon = (isActive, sortDirection, fontSize = 16) => {
  if (!isActive) {
    return (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          opacity: 0.4,
        }}
      >
        <ArrowDropUpIcon
          sx={{
            fontSize: fontSize - 2,
            marginBottom: "-6px",
          }}
        />
        <ArrowDropDownIcon sx={{ fontSize: fontSize - 2 }} />
      </span>
    );
  }
  if (sortDirection === "ASC") {
    return <ArrowDropUpIcon sx={{ fontSize }} />;
  }
  return <ArrowDropDownIcon sx={{ fontSize }} />;
};

const getGroupedDetailsSectionName = ({
  isInTransitRecoveryTab,
  isUnloadingPriorityTab,
  isDispatchPriorityTab,
  fixedColumnName,
}) => {
  if (isInTransitRecoveryTab) return "Order Details";
  if (isUnloadingPriorityTab) return "Truck Details";
  if (isDispatchPriorityTab) return "Shipment Detail";
  return fixedColumnName;
};

const resolveSortByPayload = ({ section, subKey, colOrSubKey }) => {
  if (section) return subKey;
  if (typeof colOrSubKey === "string") return colOrSubKey;
  return colOrSubKey.label;
};

const buildGroupedShipmentData = ({
  currentPageRows,
  getDetailsObject,
  getShipmentNumberFromRow,
  groupedDetailsSectionName,
  labelsEqual,
  getValueByLabel,
}) => {
  const groups = [];
  let currentGroup = null;

  currentPageRows.forEach((row, index) => {
    const details = getDetailsObject(row);

    const materialValue =
      getValueByLabel(details, "Material") ||
      getValueByLabel(row, "Material") ||
      "";

    const shipmentNumber = getShipmentNumberFromRow(row);
    const isParentRow = labelsEqual(materialValue, "Total");

    if (isParentRow) {
      const groupKey = `${groupedDetailsSectionName}-${
        shipmentNumber ? String(shipmentNumber) : "total"
      }-${index}`;

      currentGroup = {
        shipmentNumber: groupKey,
        displayShipmentNumber: shipmentNumber ? String(shipmentNumber) : "",
        totalRow: row,
        childRows: [],
        isNumericParent: true,
        rowIndex: index,
      };

      groups.push(currentGroup);
      return;
    }

    if (currentGroup) {
      currentGroup.childRows.push(row);
      return;
    }

    groups.push({
      shipmentNumber: `standalone-${index}`,
      displayShipmentNumber: shipmentNumber ? String(shipmentNumber) : "",
      totalRow: row,
      childRows: [],
      isNumericParent: false,
      rowIndex: index,
    });
  });

  return groups;
};

const buildDisplayRows = ({
  groupedShipmentData,
  expandedShipments,
  visibleChildrenCount,
}) => {
  const result = [];

  (groupedShipmentData || []).forEach((group) => {
    const isExpanded = !!expandedShipments[group.shipmentNumber];
    const allowedChildren =
      visibleChildrenCount[group.shipmentNumber] || CHILD_BATCH_SIZE;

    result.push({
      __row: group.totalRow,
      __groupKey: group.shipmentNumber,
      __isGroupHeader: true,
      __isChildRow: false,
      __hasChildren: group.isNumericParent && group.childRows.length > 0,
      __isNumericParent: group.isNumericParent,
    });

    if (group.isNumericParent && isExpanded) {
      group.childRows.slice(0, allowedChildren).forEach((child, childIndex) => {
        result.push({
          __row: child,
          __groupKey: group.shipmentNumber,
          __isGroupHeader: false,
          __isChildRow: true,
          __hasChildren: false,
          __childIndex: childIndex,
          __isNumericParent: false,
        });
      });
    }
  });

  return result;
};

const hasHiddenChildrenInExpandedGroups = ({
  groupedShipmentData,
  expandedShipments,
  visibleChildrenCount,
}) => {
  return (groupedShipmentData || []).some((group) => {
    if (!group.isNumericParent) return false;
    if (!expandedShipments[group.shipmentNumber]) return false;

    const visibleCount =
      visibleChildrenCount[group.shipmentNumber] || CHILD_BATCH_SIZE;

    return group.childRows.length > visibleCount;
  });
};

const TableShimmerBlock = ({ colSpan }) => (
  <TableRow>
    <StyledTableCell colSpan={colSpan} sx={{ padding: 0 }}>
      <div className={styles.tableShimmerBlock} />
    </StyledTableCell>
  </TableRow>
);

TableShimmerBlock.propTypes = {
  colSpan: PropTypes.number.isRequired,
};

const SPACER_SPAN_STYLE = { width: 10, display: "inline-block" };

const ExpandToggleContent = ({ meta, isExpanded, onToggle }) => {
  if (!meta.isGroupHeader || !meta.isNumericParent || !meta.hasChildren) {
    return <span style={SPACER_SPAN_STYLE} />;
  }

  return (
    <button
      onClick={(e) => onToggle(meta.groupKey, e)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        width: 10,
        height: 20,
        flexShrink: 0,
        background: "none",
        border: "none",
        padding: 0,
      }}
      type="button"
      title={isExpanded ? "Collapse" : "Expand"}
      aria-label={
        isExpanded ? "Collapse shipment group" : "Expand shipment group"
      }
    >
      {isExpanded ? (
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#667085",
          }}
        />
      ) : (
        <KeyboardArrowRightIcon
          sx={{
            fontSize: 18,
            color: "#667085",
          }}
        />
      )}
    </button>
  );
};

ExpandToggleContent.propTypes = {
  meta: PropTypes.shape({
    groupKey: PropTypes.string,
    isGroupHeader: PropTypes.bool,
    isNumericParent: PropTypes.bool,
    hasChildren: PropTypes.bool,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const LossToActionOverview = ({
  data,
  columns,
  paginationData,
  sortingData,
  handlePageChange,
  isLoading = false,
  scrollableColumnsArray = [],
  fixedColumnName,
  totalColumnName,
  tableType,
}) => {
  const [tableData, setTableData] = useState(data || []);
  const [tableColumns, setTableColumns] = useState(columns || []);
  const [sortColumn, setSortColumn] = useState(sortingData?.sortBy || null);
  const [sortDirection, setSortDirection] = useState(
    sortingData?.sortOrder || "ASC",
  );
  const [fieldSection, setFieldSection] = useState(
    sortingData?.fieldsection || "",
  );
  const [page, setPage] = useState(paginationData?.page || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    paginationData?.rowsPerPage || paginationData?.pageSize || 15,
  );
  const [expandedColumns, setExpandedColumns] = useState({});
  const [expandedShipments, setExpandedShipments] = useState({});
  const [visibleChildrenCount, setVisibleChildrenCount] = useState({});
  const [isSortingLoading, setIsSortingLoading] = useState(false);

  const fixedHeadRef = useRef(null);
  const scrollHeadRef = useRef(null);
  const fixedGroupHeaderRowRef = useRef(null);
  const fixedLeafHeaderRowRef = useRef(null);
  const scrollGroupHeaderRowRef = useRef(null);
  const scrollLeafHeaderRowRef = useRef(null);
  const fixedRowRefs = useRef([]);
  const scrollRowRefs = useRef([]);
  const sentinelRef = useRef(null);
  const rafIdRef = useRef(null);

  const isUnloadingPriorityTab = labelsEqual(tableType, "Unloading Priority");
  const isInTransitRecoveryTab = labelsEqual(tableType, "InTransit Recovery");
  const isDispatchPriorityTab = labelsEqual(tableType, "Dispatch Priority");

  const isGroupedTable =
    isUnloadingPriorityTab || isInTransitRecoveryTab || isDispatchPriorityTab;

  const shouldSpanFixedHeader =
    tableType === "INTR Recovery Material View" ||
    tableType === "Order To Allocation Overview" ||
    tableType === "Allocation To Delivery Overview";

  const groupedDetailsSectionName = useMemo(
    () =>
      getGroupedDetailsSectionName({
        isInTransitRecoveryTab,
        isUnloadingPriorityTab,
        isDispatchPriorityTab,
        fixedColumnName,
      }),
    [
      isInTransitRecoveryTab,
      isUnloadingPriorityTab,
      isDispatchPriorityTab,
      fixedColumnName,
    ],
  );

  /* ---------------- Helpers ---------------- */

  const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";
    if (value < 1000) return `${value}`;
    if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
    if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;
    return `${(value / 10000000).toFixed(1)} Cr`;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "number") {
      if (value >= 10000) return formatToCappedUnit(value);
      return value.toLocaleString("en-IN");
    }

    return value;
  };

  const safeFormatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (isObjectLike(value)) return "";
    return formatValue(value);
  };

  const isPercentageLabel = (label = "") => String(label).includes("%");

  const formatScrollableCellValue = (rawValue, colLabel, subLabel = "") => {
    const formattedValue = safeFormatValue(rawValue);
    const shouldAppendPercent =
      isPercentageLabel(subLabel) || isPercentageLabel(colLabel);

    if (
      !shouldAppendPercent ||
      formattedValue === "" ||
      formattedValue === "-"
    ) {
      return formattedValue;
    }

    const asText = String(formattedValue).trim();
    if (asText.endsWith("%")) return formattedValue;

    return `${formattedValue}%`;
  };

  const isTruncatableColumn = (label) => {
    return [
      "basepack desc",
      "format",
      "shipment date time",
      "shipment number",
      "dispatch date from sending plant",
      "dispatch time from sending plant",
      "actual transit time days",
      "ideal intransit days",
      "truck waiting time (hours)",
      "truck loading delay (hrs)",
    ].includes(normalizeLabel(label));
  };

  const getExpandKey = (sectionLabel, subKey = "") =>
    subKey ? `${sectionLabel}__${subKey}` : sectionLabel;

  const isExpandedColumn = (sectionLabel, subKey = "") =>
    !!expandedColumns[getExpandKey(sectionLabel, subKey)];

  const getColumnWidth = (sectionLabel, subKey = "") =>
    isExpandedColumn(sectionLabel, subKey)
      ? `${EXPANDED_COL_WIDTH}px`
      : `${COLLAPSED_COL_WIDTH}px`;

  const getExpandableCellStyles = (sectionLabel, subKey = "") => {
    const expanded = isExpandedColumn(sectionLabel, subKey);
    const width = getColumnWidth(sectionLabel, subKey);

    return {
      minWidth: width,
      maxWidth: width,
      width,
      whiteSpace: expanded ? "normal" : "nowrap",
      overflow: expanded ? "visible" : "hidden",
      textOverflow: expanded ? "clip" : "ellipsis",
      wordBreak: expanded ? "break-word" : "normal",
      overflowWrap: expanded ? "anywhere" : "normal",
      boxSizing: "border-box",
    };
  };

  const getSectionHeaderStyles = (sectionLabel, subKeys = []) => {
    if (!subKeys.length) return getExpandableCellStyles(sectionLabel);

    const totalWidth = subKeys.reduce((acc, sub) => {
      return (
        acc +
        (isExpandedColumn(sectionLabel, sub)
          ? EXPANDED_COL_WIDTH
          : COLLAPSED_COL_WIDTH)
      );
    }, 0);

    const width = `${totalWidth}px`;

    return {
      minWidth: width,
      maxWidth: width,
      width,
      boxSizing: "border-box",
    };
  };

  const toggleColumnExpansion = (sectionLabel, e, subKey = "") => {
    e.stopPropagation();

    const key = getExpandKey(sectionLabel, subKey);

    setExpandedColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /* ---------------- Split columns ---------------- */

  const normalizedScrollableSet = useMemo(
    () => new Set((scrollableColumnsArray || []).map(normalizeLabel)),
    [scrollableColumnsArray],
  );

  const isScrollableColumn = useCallback(
    (label) => normalizedScrollableSet.has(normalizeLabel(label)),
    [normalizedScrollableSet],
  );

  const collectSubKeysAcrossRows = useCallback((rows, colLabel) => {
    const signatureMap = new Map(); // sig -> { keys, count }

    (rows || []).forEach((row) => {
      const sectionObj = getValueByLabel(row, colLabel);
      if (!isObjectLike(sectionObj)) return;

      const keys = Object.keys(sectionObj);
      if (!keys.length) return;

      const sig = keys
        .map(normalizeLabel)
        .sort((a, b) => a.localeCompare(b))
        .join("|");

      if (signatureMap.has(sig)) {
        signatureMap.get(sig).count += 1;
      } else {
        signatureMap.set(sig, { keys, count: 1 });
      }
    });

    if (signatureMap.size === 0) return [];

    let best = null;
    for (const entry of signatureMap.values()) {
      if (!best || entry.count > best.count) best = entry;
    }
    return best.keys;
  }, []);

  const rawSampleRow = useMemo(() => tableData?.[0] || {}, [tableData]);

  const getConfiguredSubColumns = useCallback(
    (parentLabel) =>
      (tableColumns || [])
        .filter(
          (column) =>
            isSubColumnOfParent(column, parentLabel) &&
            (column.visibility ?? true),
        )
        .sort(
          (firstColumn, secondColumn) =>
            (firstColumn.DisplayOrder ?? 0) -
            (secondColumn.DisplayOrder ?? 0),
        )
        .map((column) => getSubColumnMeta(column.label)?.childLabel)
        .filter(Boolean),
    [tableColumns],
  );

  const hasConfiguredSubColumns = useCallback(
    (parentLabel) =>
      (tableColumns || []).some((column) =>
        isSubColumnOfParent(column, parentLabel),
      ),
    [tableColumns],
  );

  const getEffectiveSubColumns = useCallback(
    (parentLabel, excludeMaterial = false) => {
      let subColumns;

      if (hasConfiguredSubColumns(parentLabel)) {
        subColumns = getConfiguredSubColumns(parentLabel);
      } else {
        subColumns = collectSubKeysAcrossRows(tableData, parentLabel);

        if (!subColumns.length) {
          const sectionObject = getValueByLabel(rawSampleRow, parentLabel);
          subColumns = isObjectLike(sectionObject)
            ? Object.keys(sectionObject)
            : [];
        }
      }

      if (excludeMaterial) {
        return subColumns.filter(
          (subColumn) => !labelsEqual(subColumn, "Material"),
        );
      }

      return subColumns;
    },
    [
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
      collectSubKeysAcrossRows,
      tableData,
      rawSampleRow,
    ],
  );

  const fixedColumns = useMemo(
    () =>
      tableColumns.filter(
        (column) =>
          !isSubColumn(column.label) &&
          (column.visibility ?? true) &&
          !isScrollableColumn(column.label) &&
          !(
            hasConfiguredSubColumns(column.label) &&
            getConfiguredSubColumns(column.label).length === 0
          ),
      ),
    [
      tableColumns,
      isScrollableColumn,
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
    ],
  );

  const scrollableColumns = useMemo(
    () =>
      tableColumns.filter(
        (column) =>
          !isSubColumn(column.label) &&
          (column.visibility ?? true) &&
          isScrollableColumn(column.label) &&
          !(
            hasConfiguredSubColumns(column.label) &&
            getConfiguredSubColumns(column.label).length === 0
          ),
      ),
    [
      tableColumns,
      isScrollableColumn,
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
    ],
  );

  /* ---------------- Sync props ---------------- */

  useEffect(() => {
    setTableData(data || []);
    setIsSortingLoading(false);
    setExpandedShipments({});
    setVisibleChildrenCount({});
    fixedRowRefs.current = [];
    scrollRowRefs.current = [];
  }, [data]);

  useEffect(() => {
    setTableColumns(columns || []);
  }, [columns]);

  useEffect(() => {
    if (sortingData) {
      setSortColumn(sortingData.sortBy || null);
      setSortDirection(sortingData.sortOrder || "DESC");
      setFieldSection(sortingData.fieldsection || "");
    }
  }, [sortingData]);

  useEffect(() => {
  if (!paginationData) {
    return;
  }

  setPage(paginationData.page || 1);
  setRowsPerPage(
    paginationData.rowsPerPage || paginationData.pageSize || 15,
  );
}, [
  paginationData?.page,
  paginationData?.rowsPerPage,
  paginationData?.pageSize,
]);


  /* ---------------- Detect subcolumns ---------------- */

  const fixedSectionSubColumns = useMemo(() => {
    const map = {};

    fixedColumns.forEach((col) => {
      const shouldReadSubColumns = isGroupedTable
        ? labelsEqual(col.label, groupedDetailsSectionName)
        : labelsEqual(col.label, fixedColumnName);

      if (shouldReadSubColumns) {
        map[col.label] = getEffectiveSubColumns(col.label, isGroupedTable);
      } else {
        map[col.label] = [];
      }
    });

    return map;
  }, [
    fixedColumns,
    fixedColumnName,
    isGroupedTable,
    groupedDetailsSectionName,
    getEffectiveSubColumns,
  ]);

  const sectionSubColumns = useMemo(() => {
    const map = {};

    scrollableColumns.forEach((col) => {
      map[col.label] = getEffectiveSubColumns(col.label);
    });

    return map;
  }, [scrollableColumns, getEffectiveSubColumns]);

  /* ---------------- Sorting ---------------- */

  const handleSort = (colOrSubKey, direction, section = "", subKey = "") => {
    const sortByPayload = resolveSortByPayload({
      section,
      subKey,
      colOrSubKey,
    });

    setIsSortingLoading(true);
    setSortColumn(sortByPayload);
    setSortDirection(direction);
    setFieldSection(section);
    handleChangePage(1, sortByPayload, direction, rowsPerPage, section);
  };

  /* ---------------- Paging ---------------- */

  const currentPageRows = useMemo(() => {
    if (paginationData?.totalRecords) return tableData;

    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return tableData.slice(startIndex, endIndex);
  }, [tableData, page, rowsPerPage, paginationData]);

  const handleChangePage = (
    newPage,
    sortCol,
    sortDir,
    newRowsPerPage,
    section,
  ) => {
    setPage(newPage);

    if (handlePageChange) {
      handlePageChange(
        newPage,
        sortCol || sortColumn,
        sortDir || sortDirection,
        newRowsPerPage || rowsPerPage,
        section ?? fieldSection,
      );
    }
  };

  const handleCustomPageChange = (newPage, newPageSize) => {
    setPage(newPage);

    handleChangePage(
      newPage,
      sortColumn,
      sortDirection,
      newPageSize || rowsPerPage,
      fieldSection,
    );
  };

  const handleCustomRowsPerPageChange = (newPageSize) => {
    setRowsPerPage(newPageSize);
    setPage(1);

    handleChangePage(1, sortColumn, sortDirection, newPageSize, fieldSection);
  };

  /* ---------------- Grouping for Unloading Priority and InTransit Recovery ---------------- */

  const getDetailsObject = useCallback(
    (row) => {
      if (isGroupedTable) {
        return getValueByLabel(row, groupedDetailsSectionName) || {};
      }

      return getValueByLabel(row, fixedColumnName) || {};
    },
    [isGroupedTable, groupedDetailsSectionName, fixedColumnName],
  );

  const getShipmentNumberFromRow = useCallback(
    (row) => {
      const details = getDetailsObject(row);

      return (
        getValueByLabel(details, "Shipment Number") ||
        getValueByLabel(details, "Shipment number") ||
        ""
      );
    },
    [getDetailsObject],
  );

  const groupedShipmentData = useMemo(() => {
    if (!isGroupedTable) return null;

    return buildGroupedShipmentData({
      currentPageRows,
      getDetailsObject,
      getShipmentNumberFromRow,
      groupedDetailsSectionName,
      labelsEqual,
      getValueByLabel,
    });
  }, [
    isGroupedTable,
    currentPageRows,
    getDetailsObject,
    getShipmentNumberFromRow,
    groupedDetailsSectionName,
  ]);

  const displayRows = useMemo(() => {
    if (!isGroupedTable) return currentPageRows;

    return buildDisplayRows({
      groupedShipmentData,
      expandedShipments,
      visibleChildrenCount,
    });
  }, [
    isGroupedTable,
    currentPageRows,
    groupedShipmentData,
    expandedShipments,
    visibleChildrenCount,
  ]);

  const hasMoreHiddenChildren = useMemo(() => {
    if (!isGroupedTable) return false;

    return hasHiddenChildrenInExpandedGroups({
      groupedShipmentData,
      expandedShipments,
      visibleChildrenCount,
    });
  }, [
    isGroupedTable,
    groupedShipmentData,
    expandedShipments,
    visibleChildrenCount,
  ]);

  const toggleShipmentExpansion = (shipmentNumber, e) => {
    e?.stopPropagation?.();

    if (!shipmentNumber) return;

    setExpandedShipments((prev) => {
      const willExpand = !prev[shipmentNumber];

      if (willExpand) {
        setVisibleChildrenCount((prevVisible) => ({
          ...prevVisible,
          [shipmentNumber]: prevVisible[shipmentNumber] || CHILD_BATCH_SIZE,
        }));
      }

      return {
        ...prev,
        [shipmentNumber]: willExpand,
      };
    });
  };

  /* ---------------- Lazy render child rows on scroll ---------------- */

  const revealMoreHiddenChildren = useCallback(() => {
    setVisibleChildrenCount((prev) => {
      const next = { ...prev };

      (groupedShipmentData || []).forEach((group) => {
        if (!group.isNumericParent) return;
        if (!expandedShipments[group.shipmentNumber]) return;

        const currentVisible = next[group.shipmentNumber] || CHILD_BATCH_SIZE;

        if (group.childRows.length > currentVisible) {
          next[group.shipmentNumber] = Math.min(
            currentVisible + CHILD_BATCH_SIZE,
            group.childRows.length,
          );
        }
      });

      return next;
    });
  }, [groupedShipmentData, expandedShipments]);

  useEffect(() => {
    if (!isGroupedTable || !hasMoreHiddenChildren || !sentinelRef.current)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (!firstEntry?.isIntersecting) return;

        revealMoreHiddenChildren();
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isGroupedTable, hasMoreHiddenChildren, revealMoreHiddenChildren]);

  /* ---------------- Height sync fixed/scroll table ---------------- */

  const syncElementPairHeight = useCallback((el1, el2) => {
    if (!el1 || !el2) return;

    el1.style.height = "";
    el2.style.height = "";
    el1.style.minHeight = "";
    el2.style.minHeight = "";

    const h1 = Math.ceil(el1.getBoundingClientRect().height);
    const h2 = Math.ceil(el2.getBoundingClientRect().height);
    const maxHeight = Math.max(h1, h2);

    const maxHeightPx = `${maxHeight}px`;

    if (el1.style.height !== maxHeightPx) {
      el1.style.height = maxHeightPx;
      el1.style.minHeight = maxHeightPx;
    }

    if (el2.style.height !== maxHeightPx) {
      el2.style.height = maxHeightPx;
      el2.style.minHeight = maxHeightPx;
    }
  }, []);

  const syncTableHeights = useCallback(() => {
    syncElementPairHeight(
      fixedGroupHeaderRowRef.current,
      scrollGroupHeaderRowRef.current,
    );
    syncElementPairHeight(
      fixedLeafHeaderRowRef.current,
      scrollLeafHeaderRowRef.current,
    );

    if (
      shouldSpanFixedHeader &&
      fixedGroupHeaderRowRef.current &&
      scrollGroupHeaderRowRef.current &&
      scrollLeafHeaderRowRef.current
    ) {
      const scrollGroupTop =
        scrollGroupHeaderRowRef.current.getBoundingClientRect().top;
      const scrollLeafBottom =
        scrollLeafHeaderRowRef.current.getBoundingClientRect().bottom;
      const combinedHeight = Math.ceil(scrollLeafBottom - scrollGroupTop);
      const combinedHeightPx = `${combinedHeight}px`;

      if (
        fixedGroupHeaderRowRef.current.style.height !== combinedHeightPx
      ) {
        fixedGroupHeaderRowRef.current.style.height = combinedHeightPx;
        fixedGroupHeaderRowRef.current.style.minHeight = combinedHeightPx;
      }
    }

    const rowCount = Math.max(
      fixedRowRefs.current.length,
      scrollRowRefs.current.length,
    );

    for (let i = 0; i < rowCount; i++) {
      syncElementPairHeight(fixedRowRefs.current[i], scrollRowRefs.current[i]);
    }
  }, [syncElementPairHeight, shouldSpanFixedHeader]);

  const scheduleHeightSync = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      syncTableHeights();
      rafIdRef.current = null;
    });
  }, [syncTableHeights]);

  useLayoutEffect(() => {
    scheduleHeightSync();

    const resizeObserver = new ResizeObserver(() => {
      scheduleHeightSync();
    });

    const elementsToObserve = [
      fixedGroupHeaderRowRef.current,
      fixedLeafHeaderRowRef.current,
      scrollGroupHeaderRowRef.current,
      scrollLeafHeaderRowRef.current,
      ...fixedRowRefs.current,
      ...scrollRowRefs.current,
    ].filter(Boolean);

    elementsToObserve.forEach((el) => resizeObserver.observe(el));

    window.addEventListener("resize", scheduleHeightSync);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleHeightSync);
    };
  }, [
    scheduleHeightSync,
    displayRows,
    tableColumns,
    expandedColumns,
    expandedShipments,
    visibleChildrenCount,
    fixedSectionSubColumns,
    sectionSubColumns,
  ]);

  /* ---------------- Generic helpers ---------------- */

  const getRowPayload = (rowItem) =>
    isGroupedTable ? rowItem?.__row || {} : rowItem;

  const getRowMeta = (rowItem) => ({
    isGroupHeader: !!rowItem?.__isGroupHeader,
    isChildRow: !!rowItem?.__isChildRow,
    hasChildren: !!rowItem?.__hasChildren,
    groupKey: rowItem?.__groupKey,
    isNumericParent: !!rowItem?.__isNumericParent,
  });

  const getBodyCellStyles = (colLabel, subKey, meta, isFirstRow = false) => ({
    ...getExpandableCellStyles(colLabel, subKey),
    backgroundColor: "transparent",
    fontWeight: isFirstRow ? "700 !important" : "400 !important",
    paddingLeft:
      isGroupedTable &&
      meta?.isChildRow &&
      labelsEqual(subKey, "Shipment Number")
        ? "16px"
        : undefined,
  });

  const getFixedCellValue = (item, col, sub) => {
    const sectionObj = getValueByLabel(item, col.label);

    const rawVal = sub
      ? getValueByLabel(sectionObj, sub)
      : getValueByLabel(item, col.label);

    return safeFormatValue(rawVal);
  };

  /* ---------------- Existing table flags ---------------- */

  const totalRecords = paginationData?.totalRecords || tableData.length;
  const totalPages = paginationData?.totalPages ?? Math.ceil(totalRecords / rowsPerPage);

  const shimmerFixedColSpan =
    fixedColumns.reduce((acc, col) => {
      const subs = fixedSectionSubColumns[col.label] || [];
      return acc + (subs.length || 1);
    }, 0) + (isGroupedTable ? 1 : 0);

  const shimmerScrollableColSpan = scrollableColumns.reduce(
    (acc, col) => acc + ((sectionSubColumns[col.label] || []).length || 1),
    0,
  );

  return (
    <>
      <div className={styles.tableWrapper}>
        {/* Fixed Columns Table */}
        <TableContainer className={styles.fixedTableContainer}>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead ref={fixedHeadRef}>
              <TableRow
                ref={fixedGroupHeaderRowRef}
                className={styles.groupHeaderRow}
              >
                {isGroupedTable && (
                  <StyledTableCell
                    rowSpan={2}
                    sx={{
                      minWidth: `${EXPAND_COLUMN_WIDTH}px`,
                      maxWidth: `${EXPAND_COLUMN_WIDTH}px`,
                      width: `${EXPAND_COLUMN_WIDTH}px`,
                      padding: "8px 6px !important",
                    }}
                  />
                )}

                {fixedColumns.map((col) => {
                  const subs = fixedSectionSubColumns[col.label] || [];
                  const hasSubs = !shouldSpanFixedHeader && subs.length > 0;

                  return (
                    <StyledTableCell
                      key={col.label}
                      colSpan={hasSubs ? subs.length : 1}
                      rowSpan={hasSubs ? 1 : 2}
                      sx={{
                        ...(hasSubs
                          ? getSectionHeaderStyles(col.label, subs)
                          : getExpandableCellStyles(col.label)),
                        cursor: col.sortable ? "pointer" : "default",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 6,
                          width: "100%",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {col.label}
                        </span>

                        {!hasSubs && isTruncatableColumn(col.label) && (
                          <button
                            onClick={(e) => toggleColumnExpansion(col.label, e)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "pointer",
                              flexShrink: 0,
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                            type="button"
                            title="Expand/Collapse"
                            aria-label="Expand or collapse column"
                          >
                            <img
                              src={ExpandRight}
                              alt="expand"
                              style={{
                                width: 20,
                                height: 20,
                                transform: isExpandedColumn(col.label)
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </button>
                        )}
                      </div>
                    </StyledTableCell>
                  );
                })}
              </TableRow>

              {!shouldSpanFixedHeader && (
                <TableRow
                  ref={fixedLeafHeaderRowRef}
                  className={styles.leafHeaderRow}
                >
                  {fixedColumns.flatMap((col) => {
                    const subs = fixedSectionSubColumns[col.label] || [];

                    if (!subs.length) return [];

                    return subs.map((sub) => {
                      const isActive =
                        fieldSection === col.label && sortColumn === sub;

                      return (
                        <StyledTableCell
                          key={`${col.label}-${sub}`}
                          sx={{
                            ...getExpandableCellStyles(col.label, sub),
                            cursor: col.sortable ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (!col.sortable) return;

                            const dir = getNewSortDirection(
                              isActive,
                              sortDirection,
                            );

                            handleSort(sub, dir, col.label, sub);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 4,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <span className={styles.headerLabel}>{sub}</span>

                              {col.sortable &&
                                renderSortIcon(isActive, sortDirection, 16)}
                            </span>

                            {isTruncatableColumn(sub) && (
                              <button
                                onClick={(e) =>
                                  toggleColumnExpansion(col.label, e, sub)
                                }
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                }}
                                type="button"
                                title="Expand/Collapse"
                                aria-label="Expand or collapse column section"
                              >
                                <img
                                  src={ExpandRight}
                                  alt="expand"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    transform: isExpandedColumn(col.label, sub)
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                />
                              </button>
                            )}
                          </div>
                        </StyledTableCell>
                      );
                    });
                  })}
                </TableRow>
              )}
            </TableHead>

            <TableBody>
              {isLoading || isSortingLoading
                ? ["shimmer-a", "shimmer-b"].map((shimmerId) => (
                    <TableShimmerBlock
                      key={`fixed-shimmer-${shimmerId}`}
                      colSpan={shimmerFixedColSpan}
                    />
                  ))
                : displayRows.map((rowItem, idx) => {
                    const item = getRowPayload(rowItem);
                    const meta = getRowMeta(rowItem);
                    const isFirstRow = idx === 0;

                    let rowKey = idx;
                    if (isGroupedTable) {
                      const rowKind = meta.isChildRow
                        ? "child-fixed"
                        : "group-fixed";
                      rowKey = `${meta.groupKey}-${rowKind}-${idx}`;
                    }

                    return (
                      <TableRow
                        key={rowKey}
                        ref={(el) => {
                          fixedRowRefs.current[idx] = el;
                        }}
                      >
                        {isGroupedTable && (
                          <StyledTableCell
                            sx={{
                              minWidth: `${EXPAND_COLUMN_WIDTH}px`,
                              maxWidth: `${EXPAND_COLUMN_WIDTH}px`,
                              width: `${EXPAND_COLUMN_WIDTH}px`,
                              padding: "0 6px !important",
                              textAlign: "left",
                              backgroundColor: "transparent",
                              fontWeight: isFirstRow
                                ? "700 !important"
                                : "400 !important",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                fontWeight: isFirstRow ? 700 : 400,
                              }}
                            >
                              <ExpandToggleContent
                                meta={meta}
                                isExpanded={!!expandedShipments[meta.groupKey]}
                                onToggle={toggleShipmentExpansion}
                              />

                              {isFirstRow && (
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  TOTAL
                                </span>
                              )}
                            </div>
                          </StyledTableCell>
                        )}

                        {fixedColumns.flatMap((col) => {
                          const subs = fixedSectionSubColumns[col.label] || [];
                          const isTotalColumn = labelsEqual(
                            col.label,
                            totalColumnName,
                          );

                          if (!subs.length) {
                            const rawVal = getValueByLabel(item, col.label);
                            const val = getFixedCellValue(item, col, "");

                            return [
                              <StyledTableCell
                                key={`${col.label}-${idx}`}
                                sx={getBodyCellStyles(
                                  col.label,
                                  "",
                                  meta,
                                  isFirstRow,
                                )}
                              >
                                <span
                                  title={
                                    typeof rawVal === "number"
                                      ? rawVal
                                      : undefined
                                  }
                                  style={{
                                    fontWeight: isFirstRow ? 700 : 400,
                                  }}
                                >
                                  {isFirstRow && isTotalColumn ? "TOTAL" : val}
                                </span>
                              </StyledTableCell>,
                            ];
                          }

                          return subs.map((sub) => {
                            const sectionObj = getValueByLabel(item, col.label);
                            const rawVal = getValueByLabel(sectionObj, sub);
                            const val = getFixedCellValue(item, col, sub);

                            return (
                              <StyledTableCell
                                key={`${col.label}-${sub}-${idx}`}
                                sx={getBodyCellStyles(
                                  col.label,
                                  sub,
                                  meta,
                                  isFirstRow,
                                )}
                              >
                                <span
                                  title={
                                    typeof rawVal === "number"
                                      ? rawVal
                                      : undefined
                                  }
                                  style={{
                                    fontWeight: isFirstRow ? 700 : 400,
                                  }}
                                >
                                  {val}
                                </span>
                              </StyledTableCell>
                            );
                          });
                        })}
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Scrollable Columns Table */}
        <TableContainer className={styles.scrollableTableContainer}>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead ref={scrollHeadRef}>
              <TableRow
                ref={scrollGroupHeaderRowRef}
                className={styles.groupHeaderRow}
              >
                {scrollableColumns.map((col, colIdx) => {
                  const subs = sectionSubColumns[col.label] || [];

                  return (
                    <StyledTableCell
                      key={col.label}
                      colSpan={subs.length || 1}
                      sx={{
                        textAlign: "center",
                        fontWeight: 600,
                        ...getSectionHeaderStyles(col.label, subs),
                        backgroundColor: `${getColumnBackgroundColor(colIdx)} !important`,
                        color: `${getColumnTextColor(colIdx)} !important`,
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {col.label === "Weighted Score" ? "" : col.label}
                      </span>
                    </StyledTableCell>
                  );
                })}
              </TableRow>

              <TableRow
                ref={scrollLeafHeaderRowRef}
                className={styles.leafHeaderRow}
              >
                {scrollableColumns.flatMap((col, colIdx) => {
                  const subs = sectionSubColumns[col.label] || [];

                  if (subs.length === 0) {
                    const isActive =
                      fieldSection === col.label && sortColumn === col.label;

                    return [
                      <StyledTableCell
                        key={`${col.label}-__self`}
                        sx={{
                          textAlign: "center",
                          ...getExpandableCellStyles(col.label),
                          backgroundColor: `${getColumnBackgroundColor(colIdx)} !important`,
                          color: `${getColumnTextColor(colIdx)} !important`,
                          cursor: col.sortable ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (!col.sortable) return;

                          const newDirection = getNewSortDirection(
                            isActive,
                            sortDirection,
                          );

                          handleSort(
                            col.label,
                            newDirection,
                            col.label,
                            col.label,
                          );
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 6,
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <span>{col.label}</span>

                            {renderSortIcon(isActive, sortDirection, 18)}
                          </span>

                          {isTruncatableColumn(col.label) && (
                            <button
                              type="button"
                              onClick={(e) =>
                                toggleColumnExpansion(col.label, e)
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                background: "none",
                                border: "none",
                                padding: 0,
                              }}
                              title="Expand/Collapse"
                            >
                              <img
                                src={ExpandRight}
                                alt="expand"
                                style={{
                                  width: 20,
                                  height: 20,
                                  transform: isExpandedColumn(col.label)
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              />
                            </button>
                          )}
                        </div>
                      </StyledTableCell>,
                    ];
                  }

                  return subs.map((sub) => {
                    const isActive =
                      fieldSection === col.label && sortColumn === sub;

                    return (
                      <StyledTableCell
                        key={`${col.label}-${sub}`}
                        sx={{
                          textAlign: "center",
                          ...getExpandableCellStyles(col.label, sub),
                          backgroundColor: `${getColumnBackgroundColor(colIdx)} !important`,
                          color: `${getColumnTextColor(colIdx)} !important`,
                          cursor: col.sortable ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (!col.sortable) return;

                          const newDirection = getNewSortDirection(
                            isActive,
                            sortDirection,
                          );

                          handleSort(sub, newDirection, col.label, sub);
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 6,
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <span>{sub}</span>

                            {renderSortIcon(isActive, sortDirection, 18)}
                          </span>

                          {isTruncatableColumn(sub) && (
                            <button
                              type="button"
                              onClick={(e) =>
                                toggleColumnExpansion(col.label, e, sub)
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                background: "none",
                                border: "none",
                                padding: 0,
                              }}
                              title="Expand/Collapse"
                            >
                              <img
                                src={ExpandRight}
                                alt="expand"
                                style={{
                                  width: 20,
                                  height: 20,
                                  transform: isExpandedColumn(col.label, sub)
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              />
                            </button>
                          )}
                        </div>
                      </StyledTableCell>
                    );
                  });
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading || isSortingLoading
                ? ["shimmer-a", "shimmer-b"].map((shimmerId) => (
                    <TableShimmerBlock
                      key={`scroll-shimmer-${shimmerId}`}
                      colSpan={shimmerScrollableColSpan}
                    />
                  ))
                : displayRows.map((rowItem, idx) => {
                    const item = getRowPayload(rowItem);
                    const isFirstRow = idx === 0;

                    let rowKey = idx;
                    if (isGroupedTable) {
                      const rowKind = rowItem.__isChildRow
                        ? "child-scroll"
                        : "group-scroll";
                      rowKey = `${rowItem.__groupKey}-${rowKind}-${idx}`;
                    }

                    return (
                      <TableRow
                        key={rowKey}
                        ref={(el) => {
                          scrollRowRefs.current[idx] = el;
                        }}
                      >
                        {scrollableColumns.flatMap((col) => {
                          const subs = sectionSubColumns[col.label] || [];

                          if (!subs.length) {
                            const rawVal = getValueByLabel(item, col.label);
                            const val = formatScrollableCellValue(
                              rawVal,
                              col.label,
                            );

                            return [
                              <StyledTableCell
                                key={`${col.label}-__self-${idx}`}
                                sx={{
                                  textAlign: "center",
                                  ...getExpandableCellStyles(col.label),
                                  backgroundColor: "transparent",
                                  fontWeight: isFirstRow
                                    ? "700 !important"
                                    : "400 !important",
                                }}
                              >
                                <span
                                  title={
                                    typeof rawVal === "number"
                                      ? rawVal
                                      : undefined
                                  }
                                  style={{
                                    fontWeight: isFirstRow ? 700 : 400,
                                  }}
                                >
                                  {val}
                                </span>
                              </StyledTableCell>,
                            ];
                          }

                          return subs.map((sub) => {
                            const sectionObj = getValueByLabel(item, col.label);
                            const rawVal = getValueByLabel(sectionObj, sub);
                            const val = formatScrollableCellValue(
                              rawVal,
                              col.label,
                              sub,
                            );

                            return (
                              <StyledTableCell
                                key={`${col.label}-${sub}-${idx}`}
                                sx={{
                                  textAlign: "center",
                                  ...getExpandableCellStyles(col.label, sub),
                                  backgroundColor: "transparent",
                                  fontWeight: isFirstRow
                                    ? "700 !important"
                                    : "400 !important",
                                }}
                              >
                                <span
                                  title={
                                    typeof rawVal === "number"
                                      ? rawVal
                                      : undefined
                                  }
                                  style={{
                                    fontWeight: isFirstRow ? 700 : 400,
                                  }}
                                >
                                  {val}
                                </span>
                              </StyledTableCell>
                            );
                          });
                        })}
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {isGroupedTable && hasMoreHiddenChildren && (
        <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
      )}

      <CustomPagination
        page={page}
        pageSize={rowsPerPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handleCustomPageChange}
        onRowsPerPageChange={handleCustomRowsPerPageChange}
      />
    </>
  );
};

LossToActionOverview.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      visibility: PropTypes.bool,
      sortable: PropTypes.bool,
      DisplayOrder: PropTypes.number,
    }),
  ),
  paginationData: PropTypes.shape({
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    pageSize: PropTypes.number,
    totalRecords: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  sortingData: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
    fieldsection: PropTypes.string,
  }),
  handlePageChange: PropTypes.func,
  isLoading: PropTypes.bool,
  scrollableColumnsArray: PropTypes.array,
  fixedColumnName: PropTypes.string,
  totalColumnName: PropTypes.string,
  tableType: PropTypes.string,
};

export default LossToActionOverview;
